import { ExternalLink, Github, ZoomIn } from "lucide-react";

function Block({ title, children }) {
  return (
    <div className="border-t border-border pt-6">
      <h4 className="mb-3 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">{title}</h4>
      {children}
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function ZoomableFigure({ src, alt, title, description, onZoomImage, className = "" }) {
  return (
    <figure className={`relative overflow-hidden rounded-2xl border border-border bg-background ${className}`}>
      <img
        src={src}
        alt={alt}
        className="h-auto w-full cursor-zoom-in"
        onClick={() => onZoomImage({ src, title, description, alt })}
      />
      <button
        type="button"
        onClick={() => onZoomImage({ src, title, description, alt })}
        aria-label={`Zoom ${title.toLowerCase()}`}
        className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/80 font-mono text-[0.72rem] text-foreground shadow-lg backdrop-blur-sm transition-colors hover:border-primary/50 hover:text-primary"
      >
        <ZoomIn className="h-3.5 w-3.5" />
      </button>
    </figure>
  );
}

export function CaseStudyContent({ project, onZoomImage }) {
  return (
    <div className="space-y-7 pb-16">
      <div className="flex flex-wrap items-center gap-2">
        {project.stack.map((s) => (
          <span key={s} className="tag">
            {s}
          </span>
        ))}
      </div>

      <p className="text-[1.04rem] leading-relaxed text-foreground sm:text-[1.08rem]">{project.summary}</p>

      {project.id === "ai-document-assistant" && (
        <ZoomableFigure
          src="/chat-citations.png"
          alt="Conversational document assistant chat interface with cited answers"
          title="Chat interface"
          description="Zoomed product screenshot"
          onZoomImage={onZoomImage}
        />
      )}

      <div className="flex flex-wrap gap-3">
        {project.github && (
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-4 py-2 font-mono text-xs text-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            <Github className="h-4 w-4" /> Source
          </a>
        )}
        {project.live && (
          <a
            href={project.live}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 font-mono text-xs font-semibold text-primary-foreground"
          >
            <ExternalLink className="h-4 w-4" /> Live demo
          </a>
        )}
      </div>

      <Block title="Problem">
        <p className="text-[0.96rem] leading-relaxed text-muted-foreground">{project.problem}</p>
      </Block>
      <Block title="Motivation">
        <p className="text-[0.96rem] leading-relaxed text-muted-foreground">{project.motivation}</p>
      </Block>
      <Block title="Architecture">
        {project.id === "ai-document-assistant" && (
          <ZoomableFigure
            src="/mermaid-diagram.png"
            alt="Architecture diagram for the conversational document assistant"
            title="Architecture diagram"
            description="System architecture overview"
            onZoomImage={onZoomImage}
            className="mb-5"
          />
        )}
        <List items={project.architecture} />
      </Block>
      <Block title="Challenges">
        <List items={project.challenges} />
      </Block>
      <Block title="Solutions">
        <List items={project.solutions} />
      </Block>
      <Block title="Scalability">
        <p className="text-[0.96rem] leading-relaxed text-muted-foreground">{project.scalability}</p>
      </Block>
      {project.performance && (
        <Block title="Performance">
          <p className="text-[0.96rem] leading-relaxed text-muted-foreground">{project.performance}</p>
        </Block>
      )}
      <Block title="Impact">
        <List items={project.impact} />
      </Block>
    </div>
  );
}
